import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';

const SECTION_LABELS = {
  homepage_hero: 'Homepage Hero',
  about_natasha: 'About Natasha',
  business_hero: 'Business Page Hero',
  business_solution: 'Business Solution Section',
  personal_hero: 'Personal Page Hero',
  book_section: 'Book Section',
  contact_details: 'Contact Details',
  social_links: 'Social Links',
  programs: 'Programs / Services',
  testimonials: 'Testimonials',
};

function FieldEditor({ value, onChange }) {
  if (typeof value === 'string' && value.length > 80) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
      />
    );
  }
  if (typeof value === 'number') {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
      />
    );
  }
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
    />
  );
}

function ArrayItemEditor({ item, onFieldChange }) {
  return (
    <>
      {Object.entries(item).map(([field, value]) => (
        <div key={field}>
          <label className="block text-xs text-gray-500 mb-1 capitalize">{field.replace(/_/g, ' ')}</label>
          <FieldEditor value={value} onChange={(val) => onFieldChange(field, val)} />
        </div>
      ))}
    </>
  );
}

export default function ContentEditorPage() {
  const { api } = useAuth();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [message, setMessage] = useState('');

  const fetchContent = useCallback(() => {
    api.get('/admin/content').then(({ data }) => {
      setSections(data);
      if (data.length > 0) setExpanded({ [data[0].section_key]: true });
    }).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const updateField = (sectionIdx, field, value) => {
    setSections((prev) => {
      const updated = [...prev];
      updated[sectionIdx] = { ...updated[sectionIdx], content: { ...updated[sectionIdx].content, [field]: value } };
      return updated;
    });
  };

  const updateArrayItem = (sectionIdx, arrIdx, field, value) => {
    setSections((prev) => {
      const updated = [...prev];
      const arr = [...updated[sectionIdx].content];
      arr[arrIdx] = { ...arr[arrIdx], [field]: value };
      updated[sectionIdx] = { ...updated[sectionIdx], content: arr };
      return updated;
    });
  };

  const saveSection = async (section) => {
    setSaving(section.section_key);
    setMessage('');
    try {
      await api.put(`/admin/content/${section.section_key}`, { content: section.content });
      setMessage(`"${SECTION_LABELS[section.section_key] || section.section_key}" saved successfully`);
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setMessage('Failed to save. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const renderFields = (content, sectionIdx) => {
    if (Array.isArray(content)) {
      return content.map((item, arrIdx) => (
        <div key={`item-${arrIdx}`} className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-100">
          <p className="text-xs font-medium text-gray-400 uppercase">Item {arrIdx + 1}</p>
          <ArrayItemEditor item={item} onFieldChange={(field, val) => updateArrayItem(sectionIdx, arrIdx, field, val)} />
        </div>
      ));
    }

    return Object.entries(content).map(([field, value]) => (
      <div key={field}>
        <label className="block text-xs text-gray-500 mb-1 capitalize">{field.replace(/_/g, ' ')}</label>
        <FieldEditor value={value} onChange={(val) => updateField(sectionIdx, field, val)} />
      </div>
    ));
  };

  return (
    <AdminLayout>
      <div data-testid="admin-content-editor">
        <h1 className="text-2xl font-medium mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Content Editor</h1>
        <p className="text-sm text-gray-500 mb-6">Edit your website content</p>

        {message && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${message.includes('Failed') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`} data-testid="content-save-message">
            {message}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#C4A484] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {sections.map((section, idx) => (
              <div key={section.section_key} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`content-section-${section.section_key}`}>
                <button
                  onClick={() => toggle(section.section_key)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium">{SECTION_LABELS[section.section_key] || section.section_key}</span>
                  {expanded[section.section_key] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {expanded[section.section_key] && (
                  <div className="px-5 pb-5 space-y-4 border-t border-gray-50">
                    <div className="pt-4 space-y-4">{renderFields(section.content, idx)}</div>
                    <button
                      onClick={() => saveSection(section)}
                      disabled={saving === section.section_key}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-xs font-medium rounded-lg hover:bg-[#C4A484] transition-colors disabled:opacity-50"
                      data-testid={`save-${section.section_key}`}
                    >
                      <Save className="w-3.5 h-3.5" />
                      {saving === section.section_key ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
